import React from 'react';
import { connect, ConnectedProps } from '@cerebral/react';
import { state } from 'app/pwa/app.cerebral';
import { scaleBand, scaleLinear } from 'd3-scale';

import { useText } from 'common/hooks';

import { Wrapper, Title, Text, Line } from './elements';

type Props = {
  readonly className?: string;
  readonly height: number;
  readonly width: number;
  readonly isMobile?: boolean;
};

const deps = {
  tag: state.challenge.tagModal.key,
  organisationName: state.challenge.campaign.organisation.name,
  teamQuizStats: state.challenge.tagModal.computedTeamQuizStats,
};

function TeamQuizGraph({
  className,
  organisationName,
  teamQuizStats,
  height,
  width,
  isMobile,
}: Props & typeof deps & ConnectedProps): React.ReactElement {
  const t = useText();

  const xScale = scaleBand()
    .range([0, width])
    .domain(teamQuizStats.map(s => s.label))
    .padding(0.3);

  const yScale = scaleLinear()
    .range([height - 20, 20])
    .domain([0, Math.max(...teamQuizStats.map(s => s.value))]);

  return (
    <Wrapper className={className} lighterBorder={isMobile}>
      <Title>
        {t('challenge.quiz.end_page.compare_with_others', { organisationName })}
      </Title>
      <svg height={height} width={width}>
        <g>
          {teamQuizStats.map(entry => (
            <rect
              key={entry.key}
              x={xScale(entry.label)}
              y={yScale(entry.value)}
              width={xScale.bandwidth()}
              height={yScale(0) - yScale(entry.value) - 4}
              fill={entry.isEmphasised ? '#FBC02D' : '#CFD8DC'}
              rx={5}
            />
          ))}
        </g>
        <g>
          {teamQuizStats.map(entry => (
            <Text
              key={entry.key}
              x={xScale(entry.label) + xScale.bandwidth() / 2}
              y={yScale(entry.value) - 6}
            >
              {`${entry.value}%`}
            </Text>
          ))}
        </g>
        <g>
          <Line x1={0} x2={width} y1={height - 16} y2={height - 16} />
        </g>
        <g>
          {teamQuizStats.map(entry => (
            <Text
              key={entry.key}
              x={xScale(entry.label) + xScale.bandwidth() / 2}
              y={height}
            >
              {entry.label}
            </Text>
          ))}
        </g>
      </svg>
    </Wrapper>
  );
}

export default connect<Props, typeof deps>(deps, TeamQuizGraph);
